/**
 * The Quoting API client: a thin view of the Quote Desk engine through
 * `/api/v1/quoting/*`. Every number on the page comes from here (and so from the
 * one desk core); the frontend holds no quoting logic.
 */
import {AuthenticatedApiClient} from '@/matic/lib/api/authenticated-api-client';
import {EnhancedApiError} from '@/matic/lib/api/error-types';
import {env} from '@/matic/config/env';

/* eslint-disable @typescript-eslint/naming-convention -- wire shapes mirror the engine's JSON (snake_case) */

/** A JSON value as text: strings as-is, numbers and booleans printed, everything else empty. */
const asString = (value: unknown): string =>
  typeof value === 'string'
    ? value
    : typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : '';

export type QuoteQueueItem = {
  id: string;
  subject: string;
  customer: string | null;
  receivedAt: string | null;
  chip: string | null;
  nQuestions: number;
  engineKey: string | null;
  kw: number | null;
  /** EEAA workbooks written for the request. An export does not close the quote. */
  nExports: number;
  /** MATIC's own fields on the Quote record (a request with no record yet is open, nothing set). */
  progress: QuoteProgress;
  erpQuoteNumber: string | null;
  assignee: string | null;
  waitingOn: string | null;
  progressSource: 'manual' | 'outlook' | null;
  /** AES's ETO level, 1 (standard product) to 5 (clean-sheet), as a person set it. */
  etoLevel: EtoLevel | null;
  requestType?: string | null;
};

export type QuoteProgress = 'open' | 'done' | 'declined';

export type EtoLevel = '1' | '2' | '3' | '4' | '5';

/** The record fields a person sets on a quote; an empty string clears one. */
export type QuoteOwnedChanges = Partial<{
  progress: QuoteProgress;
  erpQuoteNumber: string;
  assignee: string;
  waitingOn: string;
  etoLevel: EtoLevel | '';
}>;

export type QuotePacket = {
  id: string;
  subject?: string;
  from?: string;
  receivedAt?: string;
  customer?: string | null;
  triage?: Record<string, unknown>;
  request?: Record<string, unknown>;
  fields?: Array<Record<string, unknown>>;
  openQuestions?: Array<Record<string, unknown>>;
  attachments?: Array<{name: string; bytes?: number}>;
  lookup?: Record<string, unknown> | null;
  raw: Record<string, unknown>;
};

/** One worksheet write a flow question offers as a click: `set` a field or `choose` a card option. */
export type QuoteFlowChoice = {
  text: string;
  input: {
    op: 'set' | 'choose' | string;
    field?: string;
    value?: unknown;
    option?: string;
    card_id?: string;
  };
};

/** A decision the flow leaves open on a step, with the choices that would close it. */
export type QuoteFlowQuestion = {
  id: string;
  field: string;
  question: string;
  evidence: string[];
  choices: QuoteFlowChoice[];
};

export type QuoteFlowStep = {
  id: string;
  title: string;
  value: string | null;
  basis: string | null;
  built: boolean;
  questions: QuoteFlowQuestion[];
  escalations: string[];
};

export type QuoteFlow = {
  requestId: string;
  lane: string;
  exportable: boolean;
  steps: QuoteFlowStep[];
  escalations: string[];
  raw: Record<string, unknown>;
};

/** A priced (or refused) accessory line from `spec.price.accessories`. */
export type QuotePriceLine = {
  lineId: string;
  pn: string | null;
  pnStatus: string;
  description: string | null;
  face: string;
  unitSell: number | null;
  ext: number | null;
  note: string | null;
};

/** The forming quote: the engine's price block, read for display only. */
export type QuotePrice = {
  withheld: boolean;
  silencer: {
    face: string;
    sell: number | null;
    hedgedSell: number | null;
  } | null;
  accessories: QuotePriceLine[];
  package: {
    face: string;
    sell: number | null;
    hedgedSell: number | null;
    excluding: string[];
    silencerQty: number | null;
  } | null;
  /** The prior quote this one is measured against, when there is one. */
  prior: {
    quoteNumber: string | null;
    date: string | null;
    aesQuotedSell: number | null;
    requote: boolean;
  } | null;
};

/** The quote header fields on the EEAA sheet a person confirms before export. */
export const EXPORT_HEADER_FIELDS = [
  'company',
  'contact',
  'address1',
  'address2',
  'phone',
  'quote_number',
  'project_reference',
  'date',
  'prepared_by',
  'salesperson',
  'ship_to_company',
  'ship_to_address1',
  'ship_to_address2',
  'lead_time',
  // The sheet's terms: prefilled from the account's history, else AES's template values.
  'shipping_method',
  'shipping_terms',
  'fob_point',
  'terms_and_conditions',
  'payment_terms',
  'notes',
] as const;

export type ExportHeaderField = (typeof EXPORT_HEADER_FIELDS)[number];

export type QuoteExportPlan = {
  exportable: boolean;
  filename: string;
  header: Record<ExportHeaderField, string>;
  /** Where each prefilled header value came from, as the engine states it. */
  sources: Record<ExportHeaderField, string>;
};

/** Where the request lives as a MATIC record. */
export type AccessorySelectionAction = {
  pn: string;
  lineId: string;
  revision: string;
  quantity?: number;
};
export type AccessorySelectionPreview = {
  pn: string;
  description: string | null;
  revision: string;
  unavailable_reason: string | null;
  targets: Array<{
    line_id: string;
    category: string;
    type: string;
    add: boolean;
    qty: number | null;
    current_pn: string | null;
    status: 'match' | 'unknown' | 'different';
    reasons: string[];
  }>;
};

/** Read-only PN detail; quote prices are historical, never a current offer. */
export type QuotePartDetail = {
  status: 'found' | 'none' | 'not_a_part';
  core: string | null;
  description: string | null;
  n_quotes: number;
  lines: Array<{
    job_key: string;
    quote_number: string | null;
    customer: string | null;
    engine_key: string | null;
    date: string | null;
    description: string | null;
    qty: number | null;
    unit_price_usd: number | null;
  }>;
};

/**
 * A part's sales outline print, as the engine's index names it: the latest revision in AES's
 * sales outline folder, `none` when the folder has no print for the part, or null when the
 * part number has no core (a placeholder) or the engine carries no index.
 */
export type QuoteOutlinePrint =
  | {
      status: 'found';
      pn: string;
      rev: string;
      file: string;
      /** AES's SharePoint copy, for when the print is not staged on the server. */
      url: string;
      description: string | null;
      engine_make: string | null;
      engine_model: string | null;
      weight_lbs: number | null;
    }
  | {status: 'none'; pn: string}
  | null;

export type QuoteRecordRef = {
  objectTypeId: string;
  recordId: string;
  code: string;
};

/** The desk's `tail_marks.json` (skill clean-thread): per message, where each closing/signature sits, in thread.txt line numbers. */
export type QuoteTailMarks = {
  messages: Array<{
    message: number;
    status?: string;
    tails: Array<{start_line: number; end_line: number | null}>;
  }>;
};

/** The answer to importing a Relay thread: where the read landed. */
export type QuoteThreadImport = {
  slug: string;
  runId: string;
  subject: string;
  attachments: string[];
  skippedAttachments: string[];
};

/**
 * Triage's "Quote this thread" on a host running hosted intake: the thread is queued for an
 * unattended full read that skips the keyword screen and quick pass. `intake_disabled` means
 * this deployment has no hosted intake, so the caller falls back to {@link QuoteThreadImport}.
 */
export type QuoteThreadPromotion =
  | {status: 'intake_disabled'}
  | {status: 'pending' | 'retry' | 'reading'; jobId: string};

/** One accessory line of the worksheet (`accessories.lines[]`). */
export type QuoteAccessoryLine = {
  id: string;
  category: string;
  type: string;
  tag: string;
  qty: number | null;
  qtyBasis: string;
  side: string;
  from: string;
  /** Evidence the engine shows beside the line, grey. */
  note: string | null;
  /** The line's material and how it was closed (`chosen` when pinned on the worksheet). */
  material: string | null;
  materialBasis: string;
  /** A customer habit set the material: the knowledge card, and the house material it replaced. */
  habit: {card: string; was: string} | null;
  /** A material the request names without a grade ("stainless steel"): its words, and where. */
  askedMaterial: {asWritten: string; from: string | null} | null;
  /** A "+ Custom" part a person added with their own words. */
  custom: boolean;
  /** The line's least settled value (Settled, Likely, Split, Limited): a line is no surer than that. */
  state: string | null;
  /** The line's dimensions as the engine carries them; null where the part has none. */
  dims: QuoteAccessoryDims;
  /**
   * A catalogue part the engine took by its description and did not break into sizes (its
   * config has no `parsed`): the description's size lines, as the quote sheet prints them.
   */
  catalogueText: string | null;
  /** How each of the line's spec values was closed (`stated`, `chosen`, `cell`, `customer`, `default`), by key. */
  specBases: Record<string, string>;
  /** A part chosen for the line on the worksheet: from AES's catalogue, or a precedent configuration. */
  configSource: 'catalogue' | 'precedent' | null;
};

/** `accessories.base`: which rung of the engine's order started the package, and from what. */
export type QuoteAccessoryBase = {
  rung:
    | 'prior_quote'
    | 'customer_engine'
    | 'customer_modal'
    | 'customer_latest'
    | 'house_standard';
  why: string;
  quote: string | null;
  date: string | null;
  /** How many of this customer's prior quotes carried accessories: the pool the rung chose from. */
  nPriorJobs: number;
  /** The part types the starting set carries (`types` + `all_types`): what the engine suggested. */
  types: string[];
  /** The start quote's job, when the history row joins to one: the citation opens into it. */
  jobKey: string | null;
  /** The pool's jobs that join to a job key, newest first. */
  poolJobKeys: string[];
  /** `customer_engine` only: the customer's quotes on this engine, newest first — that rung's pool. */
  engineJobKeys: string[];
};

export type QuoteAccessoryDims = {
  /** The part's own Ø (a kit's joint, an elbow's bend, a rain cap). */
  diaIn: number | null;
  /** A flexible's silencer end: the one size a person can pin on the line (`accessories.dia`). */
  silencerEndIn: number | null;
  engineEndIn: number | null;
  engineFlange: string | null;
  flange: string | null;
  lengthIn: number | null;
  legsIn: number[] | null;
  termination: string | null;
  elementMaterial: string | null;
  /** The silencer-end connection family (ANSI, CAT, …) on a flexible. */
  silencerConnection: string | null;
  /** An elbow's bend angle, the header's form word (MITERED, MANDREL) and its rain guard. */
  angleDeg: number | null;
  style: string | null;
  rainGuard: boolean | null;
  rgLengthIn: number | null;
};

/** An accessory the engine took out, and why (`accessories.removed`). */
export type QuoteAccessoryRemoved = {
  category: string;
  /** The tag the line would have carried had it stayed. */
  tag: string;
  why: string;
  /** The customer's words, when a strike or a stated mitered outlet removed it. */
  words: string | null;
  from: string | null;
};

export type WorksheetCard = {
  id: string;
  stage: string;
  field: string;
  question: string;
  evidence: string[];
  options: Array<{id: string; label: string}>;
};

export type AccessoryConfiguration = {
  partNumber: string;
  description: string;
  jobs: number;
  last: string;
  fit: string;
  source: string;
  ref: Record<string, unknown>;
};

export type CatalogueQuery = {
  type: 'bellows' | 'elbow' | 'y';
  engine: {dia: number | null; standard: string | null};
  silencer: {dia: number | null; standard: string | null};
  material: string | null;
  length: {
    mode: 'any' | 'required' | 'preferred' | 'range';
    value?: number;
    min?: number;
    max?: number;
  };
  leg1?: number | null;
  leg2?: number | null;
  angle_deg?: number | null;
  termination?: string | null;
};

export type CataloguePart = {
  pn: string;
  description: string;
  status: 'match' | 'unknown' | 'different';
  reasons: string[];
  missing_fields: string[];
  length_in: number | null;
  quote_count: number;
  last: string | null;
  ref: Record<string, unknown>;
};

export type AccessoryPrecedent = {
  scopes: Array<{scope: string; jobs: number}>;
  types: Array<{type: string; configurations: AccessoryConfiguration[]}>;
};

/** Who wrote a worksheet event: a person (`aes`, `matic`) or Otto (`claude`). */
export type WorksheetActor = {name: string; kind: string};

export type WorksheetFlag = {
  kind?: 'review';
  shown?: string;
  stage?: string;
  eventId?: string;
  field?: string;
  note?: string;
  by?: WorksheetActor;
  at?: string;
};

export type EngineConnectionOption = {
  id: string;
  diameter_in: number;
  standard: string;
  jobs: number;
  same_rating_jobs: number;
  last: string;
};
export type EngineConnectionChoices = {
  scope: {engine_key: string; kw: number};
  options: EngineConnectionOption[];
  revision: string;
  has_override: boolean;
};
export type EngineConnectionChoice = {
  scope: 'default' | 'request' | 'inherit';
  revision: string;
  option_id?: string;
  custom?: {diameter_in: number; standard: string};
  /** The engine's turbo outlets, saved with the connection; omitted keeps none. */
  turbos?: number;
};
/** This request's turbo count, chosen apart from its connection (`worksheet.selectEngineTurbos`);
 * `inherit` returns to precedent. */
export type EngineTurbosChoice = {
  revision: string;
  turbos?: number;
  inherit?: true;
};

export type WorksheetState = {
  requestId: string;
  lane: string;
  exportable: boolean;
  held: unknown;
  pins: Array<Record<string, unknown>>;
  cards: WorksheetCard[];
  flags: WorksheetFlag[];
  pinConflicts: unknown[];
  spec: Record<string, unknown>;
  accessories: Record<string, unknown>;
  raw: Record<string, unknown>;
};

/** One pin of the worksheet's event log, as the engine folds it. */
export type WorksheetPin = {
  field: string;
  value: unknown;
  basis: string;
  note: string;
  eventId: string;
  by: WorksheetActor;
  at: string;
  stale: boolean;
};

/** The hold on a quote, when one is in force. */
export type WorksheetHold = {
  eventId: string;
  by: WorksheetActor;
  at: string;
  reason: string;
  questions: string[];
};

/** One row of a class ladder: a standard-silencer row scored against the ask. */
export type QuoteLadderRow = {
  rowId: string;
  body: string;
  classNum: number | null;
  classLabel: string;
  partNumber: string | null;
  geometry: string;
  insulation: string | null;
  actualIlDb: number | null;
  treatedDbA: number | null;
  marginDb: number | null;
  admitted: boolean | null;
  verdict: string | null;
  marked: string | null;
  /** Jobs AES built from this row on this engine, when the ladder knows. */
  aesJobs: number | null;
};

/** How a past request sits against this one: the same required reduction, stricter, easier, or at a higher rating. */
/** `same_class`: another job behind a class's price, whatever it asked. */
export type QuoteEvidenceGroup =
  | 'similar'
  | 'stricter'
  | 'easier'
  | 'higher'
  | 'same_class';

/** One past request near this ask, joined to its history row. */
export type QuoteEvidenceRequest = {
  group: QuoteEvidenceGroup;
  jobKey: string;
  quoteNumber: string | null;
  date: string | null;
  customer: string | null;
  kw: number | null;
  targetDbA: number | null;
  distanceFt: number | null;
  /** The loss it needed from this rating's sound source, and its difference from this request's. */
  neededDb: number | null;
  deltaDb: number | null;
  body: string;
  classNum: number | null;
  requirement: string | null;
  dims: string | null;
  price: number | null;
  /** The engine it was quoted for: another family member's on a pooled list. */
  engineKey: string | null;
};

/** `requestClassEvidence`: the evidence the desk draws under and beside the class strip. */
export type QuoteClassEvidence = {
  sameDb: number | null;
  /** How far a Stricter or Easier ask may sit from this one's needed reduction. */
  nearbyDb: number | null;
  thisNeededDb: number | null;
  requests: QuoteEvidenceRequest[];
  /** The request's engine, and the family its past requests were pooled from when any is another engine's. */
  engineKey: string | null;
  family: string | null;
  /** Historical silencer-only price per class on the ladder (original prices, not repriced). */
  prices: Array<{
    body: string;
    classNum: number;
    median: number | null;
    low: number | null;
    high: number | null;
    total: number;
  }>;
  /** The exhaust spectrum through each ladder row, band by band, as the EEAA workbook computes it. */
  bands: QuoteBandCalc | null;
};

/** One ladder row through the workbook's octave-band arithmetic. */
export type QuoteBandRow = {
  rowId: string;
  body: string;
  classNum: number;
  insulation: string | null;
  partNumber: string | null;
  /** Transmission loss, 63 Hz to 8 kHz. */
  tl: Array<number | null>;
  /** Exhaust less the silencer's loss, per band, at the source's reference distance. */
  treatedBands: Array<number | null>;
  /** Overall treated level at the request's distance, the silencer's insertion loss, the margin to target. */
  treatedDbA: number | null;
  actualIlDb: number | null;
  marginDb: number | null;
};

/** The sound source the ladder sized from, run through every ladder row (`acousticPerformance`). */
export type QuoteBandCalc = {
  hz: number[];
  sourceId: string;
  refFt: number | null;
  distanceFt: number | null;
  /** Spherical spreading from the reference distance to the request's: subtracted from every band. */
  divergenceDb: number | null;
  /** The exhaust at the reference distance, 63 Hz to 8 kHz, and its overall. */
  rawBands: Array<number | null>;
  rawDbA: number | null;
  targetDbA: number | null;
  requiredIlDb: number | null;
  /**
   * What the customer's sheet gave, when the raw bars are it: power or pressure, A or linear,
   * at its own distance. Null for AES's own data, which keeps only the normalized row.
   */
  asGiven: {
    quantity: 'SPL' | 'PWL';
    weighting: 'A' | 'LIN';
    distanceFt: number;
    from: string;
  } | null;
  rows: QuoteBandRow[];
};

/** `requestClassLadder`: the sound-and-class ladder exactly as the request view computes it. */
export type QuoteClassLadder = {
  status: string;
  code: string | null;
  sound: {sourceId: string | null; status: string; caveat: string | null};
  ask: {
    lane: string;
    targetDbA: number | null;
    distanceFt: number | null;
    distanceBasis: string;
    reductionDb: number | null;
    grade: string | null;
    /** The body the customer wrote ("disc"), when the read found one. */
    statedShape: string | null;
    location: string | null;
    /** `stated` or `assumed`: whether the location the ladder used came from the request. */
    locationBasis: string;
    targetUsed: number | null;
    requiredIlDb: number | null;
    sourceLevel: {rawDbA: number; refDistanceFt: number} | null;
  };
  recommendedBody: string | null;
  classNum: number | null;
  rowId: string | null;
  proposal: {
    body: string;
    classNum: number;
    rowId: string;
    rowBasis: string;
  } | null;
  alternatives: Array<{
    body: string;
    classNum: number;
    rowId: string;
    rowBasis: string;
  }>;
  ladders: Record<string, QuoteLadderRow[]>;
  openSlots: Array<{
    id: string;
    field: string;
    question: string;
    wouldCloseIt: string;
    disposition: string;
  }>;
  /** AES's usual class in this cell, from the decision context. */
  aesUsual: {classLabel: string; nJobs: number; share: number} | null;
  raw: Record<string, unknown>;
};

/** One sound source the engine could score an ask against. */
export type QuoteSoundCandidate = {
  id: string;
  sourceKind: string;
  kw: number | null;
  /** The nine octave slots the data file stores, in slot order; null where the sheet left a band blank. */
  bandsDbA: Array<number | null>;
  engineKey?: string;
  sourceRef?: string;
  reviewReason?: string | null;
  automaticEligible?: boolean;
  provenance?: Record<string, unknown>;
  overallDbA: number | null;
  quantity: string | null;
  distanceFt: number | null;
  nWorkbooks: number | null;
  share: number | null;
  latestAgrees: boolean | null;
  firstSeen: string | null;
  lastSeen: string | null;
};

/** One past AES quote as the `job` tool reads it: who, when, and the part number it carried. */
export type QuoteJobSummary = {
  jobKey: string;
  quoteNumber: string | null;
  customer: string | null;
  date: string | null;
  partNumber: string | null;
  /** The silencer's mount as the quote recorded it (`TOP MNTG`, `BOTTOM MNTG`, `NO MNTG`). */
  mounting: string | null;
  /** What the quote was built for: its engine, requirement ("Class 4") and shape ("Rectangular"). */
  engineKey: string | null;
  kw: number | null;
  requirement: string | null;
  silencerType: string | null;
  /** Every part number on the quote's printed lines (silencer and accessories). */
  linePartNumbers: string[];
  /** The category of each of the quote's printed lines (`BELLOWS`, `RAINCAP`, …): what it included. */
  lineCategories: string[];
};

/** `resolveSound`: which spectrum the engine reads for an engine at a rating, and why. */
export type QuoteSound = {
  status: string;
  engineKey: string;
  requestedKw: number;
  selected: QuoteSoundCandidate | null;
  alternatives: QuoteSoundCandidate[];
  selectionBasis: string | null;
  caveat: string | null;
  openQuestion: string | null;
};

type WireQueueItem = {
  request_type?: string | null;
  id: string;
  subject: string;
  customer: string | null;
  received_at: string | null;
  chip: string | null;
  n_questions: number;
  engine_key: string | null;
  kw: number | null;
  n_exports?: number;
  progress?: QuoteProgress | null;
  erp_quote_number?: string | null;
  assignee?: string | null;
  waiting_on?: string | null;
  progress_source?: 'manual' | 'outlook' | null;
  eto_level?: EtoLevel | null;
};

/**
 * A body's inches as the history stores them — `[46.0, 14.0]` as JSON text, an array, or plain
 * text — in the quoter's form: `46 × 14`. Null when the history has none.
 */
function bodyDims(value: unknown): string | null {
  let dims = value;
  if (typeof value === 'string' && value.trim().startsWith('[')) {
    try {
      dims = JSON.parse(value) as unknown;
    } catch {
      return value;
    }
  }

  if (Array.isArray(dims))
    return dims.length > 0
      ? dims
          .map((d) => (typeof d === 'number' ? String(d) : asString(d)))
          .join(' × ')
      : null;
  return asString(dims) || null;
}

const numberList = (value: unknown): Array<number | null> =>
  Array.isArray(value) ? value.map((v) => asNumber(v)) : [];

function readAsGiven(value: unknown): QuoteBandCalc['asGiven'] {
  if (!value || typeof value !== 'object') return null;
  const g = asRecord(value);
  const distanceFt = asNumber(g.distance_ft);
  if (g.quantity !== 'SPL' && g.quantity !== 'PWL') return null;
  if (g.weighting !== 'A' && g.weighting !== 'LIN') return null;
  if (distanceFt === null) return null;
  return {
    quantity: g.quantity,
    weighting: g.weighting,
    distanceFt,
    from: asString(g.from),
  };
}

function readBandCalc(value: unknown): QuoteBandCalc | null {
  if (!value || typeof value !== 'object') return null;
  const b = asRecord(value);
  return {
    hz: numberList(b.hz).filter((n): n is number => n !== null),
    sourceId: asString(b.source_id),
    refFt: asNumber(b.ref_ft),
    distanceFt: asNumber(b.distance_ft),
    divergenceDb: asNumber(b.divergence_db),
    rawBands: numberList(b.raw_bands),
    rawDbA: asNumber(b.raw_db_a),
    targetDbA: asNumber(b.target_db_a),
    requiredIlDb: asNumber(b.required_il_db),
    asGiven: readAsGiven(b.as_given),
    rows: asList(b.rows).map((r) => ({
      rowId: asString(r.row_id),
      body: asString(r.body),
      classNum: asNumber(r.class_num) ?? 0,
      insulation: asString(r.insulation) || null,
      partNumber: asString(r.part_number) || null,
      tl: numberList(r.tl),
      treatedBands: numberList(r.treated_bands),
      treatedDbA: asNumber(r.treated_db_a),
      actualIlDb: asNumber(r.actual_il_db),
      marginDb: asNumber(r.margin_db),
    })),
  };
}

const optionLabel = (o: Record<string, unknown>): string =>
  asString(o.label) || asString(o.text) || asString(o.title) || asString(o.id);

const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asList = (value: unknown): Array<Record<string, unknown>> =>
  Array.isArray(value) ? value.map((item) => asRecord(item)) : [];

const asStrings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map((item) => asString(item)).filter(Boolean)
    : [];

/** `spec.price` → the display shape; absent or malformed blocks read as null, never as 0. */
export function readQuotePrice(
  spec: Record<string, unknown>
): QuotePrice | null {
  const {price} = spec;
  if (!price || typeof price !== 'object') return null;
  const p = asRecord(price);
  const silencer = p.silencer ? asRecord(p.silencer) : null;
  const pkg = p.package ? asRecord(p.package) : null;
  const prior = p.then ? asRecord(p.then) : null;
  return {
    withheld: Boolean(p.withheld),
    silencer: silencer
      ? {
          face: asString(silencer.face),
          sell: asNumber(silencer.sell),
          hedgedSell: asNumber(silencer.hedged_sell),
        }
      : null,
    accessories: asList(p.accessories).map((line) => ({
      lineId: asString(line.line_id),
      pn: asString(line.pn) || null,
      pnStatus: asString(line.pn_status),
      description: asString(line.description) || null,
      face: asString(line.face),
      unitSell: asNumber(line.unit_sell),
      ext: asNumber(line.ext),
      note: asString(line.note) || null,
    })),
    package: pkg
      ? {
          face: asString(pkg.face),
          sell: asNumber(pkg.sell),
          hedgedSell: asNumber(pkg.hedged_sell),
          excluding: asStrings(pkg.excluding),
          silencerQty: asNumber(pkg.silencer_qty),
        }
      : null,
    prior: prior
      ? {
          quoteNumber: asString(prior.quote_number) || null,
          date: asString(prior.date) || null,
          aesQuotedSell: asNumber(prior.aes_quoted_sell),
          requote: Boolean(prior.requote),
        }
      : null,
  };
}

/** `accessories.lines[]` → the display rows. */
export function readAccessoryLines(
  accessories: Record<string, unknown>
): QuoteAccessoryLine[] {
  return asList(accessories.lines).map((line) => ({
    id: asString(line.id),
    category: asString(line.category),
    type: asString(line.type),
    tag: asString(line.tag),
    qty: asNumber(line.qty),
    qtyBasis: asString(line.qty_basis),
    side: asString(line.side),
    from: asString(line.from),
    note: asString(line.note) || null,
    material: asString(asRecord(asRecord(line.spec).material).value) || null,
    materialBasis: asString(asRecord(asRecord(line.spec).material).basis),
    askedMaterial: line.asked_material
      ? {
          asWritten: asString(asRecord(line.asked_material).as_written),
          from: asString(asRecord(line.asked_material).from) || null,
        }
      : null,
    habit: line.habit
      ? {
          card: asString(asRecord(line.habit).card),
          was: asString(asRecord(line.habit).was),
        }
      : null,
    custom: line.custom === true,
    state: worstState(asRecord(line.spec)),
    dims: readAccessoryDims(
      asRecord(line.spec),
      asRecord(asRecord(line.config).parsed)
    ),
    catalogueText: catalogueText(asRecord(line.config)),
    specBases: specBases(asRecord(line.spec)),
    configSource: line.config
      ? asString(asRecord(line.config).source) === 'catalogue'
        ? 'catalogue'
        : 'precedent'
      : null,
  }));
}

/** The basis of every spec value the line carries (keys with a value only). */
function specBases(spec: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(spec)) {
    const slot = asRecord(value);
    const basis = asString(slot.basis);
    // A stated value with no figure yet (a stainless with its grade open) is still stated.
    if (
      ((slot.value !== null && slot.value !== undefined) ||
        basis === 'stated') &&
      basis
    )
      out[key] = basis;
  }

  return out;
}

const BASE_RUNGS = new Set([
  'prior_quote',
  'customer_engine',
  'customer_modal',
  'customer_latest',
  'house_standard',
]);

export function readAccessoryBase(
  accessories: Record<string, unknown>
): QuoteAccessoryBase | null {
  const base = asRecord(accessories.base);
  const rung = asString(base.rung);
  if (!BASE_RUNGS.has(rung)) return null;
  return {
    rung: rung as QuoteAccessoryBase['rung'],
    why: asString(base.why),
    quote: asString(base.quote) || null,
    date: asString(base.date) || null,
    nPriorJobs: asNumber(base.n_prior_jobs) ?? 0,
    types: [
      ...new Set([...asStrings(base.types), ...asStrings(base.all_types)]),
    ],
    jobKey: asString(base.job_key) || null,
    poolJobKeys: asStrings(base.pool_job_keys),
    engineJobKeys: asStrings(base.engine_job_keys),
  };
}

/** `text:CLAMP: V-BAND Ø6.00⏎6.75 20°⏎…` with no `parsed` → "V-BAND Ø6.00 · 6.75 20° · …". */
function catalogueText(config: Record<string, unknown>): string | null {
  if (config.parsed) return null;
  const key = asString(config.key);
  if (!key.startsWith('text:')) return null;
  const text = key
    .slice('text:'.length)
    .split(/\r?\n/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part, index) =>
      // The first line leads with the category ("CLAMP: V-BAND Ø6.00"); keep what follows.
      index === 0 ? part.replace(/^[^:]+:\s*/, '') : part
    )
    .join(' · ');
  return text || null;
}

/** A spec value's own value (`{value, basis, state}`). */
const specValue = (spec: Record<string, unknown>, key: string): unknown =>
  asRecord(spec[key]).value;

/**
 * A line's sizes: the engine's own spec values first, then what it parsed off the chosen
 * part's description (`config.parsed`, the engine's `AesLine`) for the fields the spec does
 * not carry — the angle, style and rain guard, and a part's ends the spec left blank.
 */
function readAccessoryDims(
  spec: Record<string, unknown>,
  parsed: Record<string, unknown>
): QuoteAccessoryDims {
  const legs = specValue(spec, 'legs') ?? parsed.legs;
  // `a` is the engine end, `b` the silencer end, as the engine's `AesLine` names them.
  const a = asRecord(parsed.a);
  const b = asRecord(parsed.b);
  const numberOf = (key: string, fallback: unknown) =>
    asNumber(specValue(spec, key)) ?? asNumber(fallback);
  const textOf = (key: string, fallback: unknown) =>
    asString(specValue(spec, key)) || asString(fallback) || null;
  return {
    diaIn: numberOf('dia', parsed.dia),
    silencerEndIn: numberOf('dia_silencer_side', b.dia),
    engineEndIn: numberOf('dia_engine_side', a.dia),
    engineFlange: textOf('engine_flange_type', a.token ?? a.family),
    flange: textOf('flange_type', null),
    lengthIn: numberOf('length_in', parsed.length_in),
    legsIn: Array.isArray(legs)
      ? legs.map((leg) => asNumber(leg)).filter((leg) => leg !== null)
      : null,
    termination: textOf('termination', parsed.termination),
    elementMaterial: textOf('element_material', null),
    silencerConnection: textOf('silencer_connection', b.token ?? b.family),
    angleDeg: asNumber(parsed.angle_deg),
    style: asString(parsed.style) || null,
    rainGuard:
      typeof parsed.rain_guard === 'boolean' ? parsed.rain_guard : null,
    rgLengthIn: asNumber(parsed.rg_length_in),
  };
}

const STATE_RANK = ['Settled', 'Likely', 'Split', 'Limited'];

/** The least settled state among a line's spec values, as the engine's own readback ranks them. */
function worstState(spec: Record<string, unknown>): string | null {
  let worst: string | null = null;
  for (const value of Object.values(spec)) {
    const state = asString(asRecord(value).state);
    if (
      STATE_RANK.includes(state) &&
      (worst === null || STATE_RANK.indexOf(state) > STATE_RANK.indexOf(worst))
    )
      worst = state;
  }

  return worst;
}

export function readAccessoryRemoved(
  accessories: Record<string, unknown>
): QuoteAccessoryRemoved[] {
  return asList(accessories.removed).map((item) => ({
    category: asString(item.category),
    tag: asString(item.tag_it_would_have_had),
    why: asString(item.why),
    words: asString(item.words) || null,
    from: asString(item.from) || null,
  }));
}

const asActor = (value: unknown): WorksheetActor => {
  const actor = asRecord(value);
  return {name: asString(actor.name) || '?', kind: asString(actor.kind)};
};

/** `pins[]` → typed rows; the engine folds each pin with the actor who wrote it. */
export function readPins(worksheet: WorksheetState): WorksheetPin[] {
  return worksheet.pins.map((pin) => ({
    field: asString(pin.field),
    value: pin.value,
    basis: asString(pin.basis),
    note: asString(pin.note),
    eventId: asString(pin.event_id),
    by: asActor(pin.by),
    at: asString(pin.at),
    stale: Boolean(pin.stale),
  }));
}

/** `held` → the hold in force, or null. */
export function readHeld(worksheet: WorksheetState): WorksheetHold | null {
  if (!worksheet.held || typeof worksheet.held !== 'object') return null;
  const held = asRecord(worksheet.held);
  return {
    eventId: asString(held.event_id),
    by: asActor(held.by),
    at: asString(held.at),
    reason: asString(held.reason),
    questions: asStrings(held.questions),
  };
}

const readLadderRow = (row: Record<string, unknown>): QuoteLadderRow => ({
  rowId: asString(row.row_id),
  body: asString(row.body),
  classNum: asNumber(row.class_num),
  classLabel: asString(row.class_label),
  partNumber: asString(row.part_number) || null,
  geometry: asString(row.geometry),
  insulation: asString(row.insulation) || null,
  actualIlDb: asNumber(row.actual_il_db),
  treatedDbA: asNumber(row.treated_db_a),
  marginDb: asNumber(row.margin_db),
  admitted: typeof row.admitted === 'boolean' ? row.admitted : null,
  verdict: asString(row.verdict) || null,
  marked: asString(row.marked) || null,
  aesJobs: asNumber(asRecord(row.aes).engine_count),
});

const readProposal = (value: unknown) => {
  const p = asRecord(value);
  const classNum = asNumber(p.class_num);
  return asString(p.body) && classNum !== null
    ? {
        body: asString(p.body),
        classNum,
        rowId: asString(p.row_id),
        rowBasis: asString(p.row_basis),
      }
    : null;
};

const readSoundCandidate = (value: unknown): QuoteSoundCandidate | null => {
  if (!value || typeof value !== 'object') return null;
  const c = asRecord(value);
  return {
    id: asString(c.id),
    sourceKind: asString(c.source_kind),
    engineKey: asString(c.engine_key),
    sourceRef: asString(asRecord(c.provenance).example_workbook),
    reviewReason: asString(c.review_reason) || null,
    automaticEligible: c.automatic_eligible !== false,
    provenance: asRecord(c.provenance),
    kw: asNumber(c.kw),
    bandsDbA: Array.isArray(c.bands_db_a)
      ? c.bands_db_a.map((band) => asNumber(band))
      : [],
    overallDbA: asNumber(c.overall_db_a),
    quantity: asString(c.quantity) || null,
    distanceFt: asNumber(c.distance_ft),
    nWorkbooks: asNumber(c.n_workbooks),
    share: asNumber(c.share),
    latestAgrees: typeof c.latest_agrees === 'boolean' ? c.latest_agrees : null,
    firstSeen: asString(c.first_seen) || null,
    lastSeen: asString(c.last_seen) || null,
  };
};

export type MailboxJob = {
  id: string;
  status: string;
  published_ids: string[];
  error: string | null;
};

export type MailboxRow = {
  flag_status: string | null;
  categories: string[];
  modified_at: string | null;
  outlook_mapping: {
    progress: string[];
    assignees: string[];
    waiting_on: string[];
    unmapped_categories: string[];
    conflicts: string[];
  };
  graph_id: string;
  internet_message_id: string | null;
  received_at: string;
  sender: string;
  subject: string;
  has_attachments: boolean;
  presence: 'new' | 'present' | 'unknown';
  thread_id: string | null;
  job: MailboxJob | null;
  published_ids: string[];
  selectable: boolean;
  unavailable_reason: string | null;
  outcome?: 'imported' | 'enqueued' | 'already_present';
};

export type MailboxWindow = {from: string; to: string};
export type ManagementIntake = {
  id: string;
  thread_id: string;
  subject: string;
  status: string;
  created_at: string;
  error: string | null;
  related_quote_ids: string[];
  /** Outlook closed this mail before it had a quote (live sync); applied when one publishes. */
  outlook_progress: 'done' | 'declined' | null;
  /** The owner's Outlook category on that mail, as it would set a quote's Owner. */
  outlook_owner: string | null;
};
/** How mail reaches Quotes on this host (`GET /intake/settings`). */
export type IntakeSettings = {
  /** The shared Inbox is polled continuously, so the manual test import is refused. */
  mailboxPolling: boolean;
  /** Screened mail is read unattended; otherwise a person clicks Read on each request. */
  autoRead: boolean;
};
export type MailboxPreview = MailboxWindow & {
  mailbox: string;
  truncated: boolean;
  rows: MailboxRow[];
  import_available: boolean;
  unavailable_reason: string | null;
};

export type QuoteReadReview = {
  id: string;
  request_id: string;
  status: 'queued' | 'reading' | 'ready' | 'applied' | 'discarded' | 'failed';
  guidance: string;
  error?: string | null;
  receipt?: {projection_status?: string; no_changes?: boolean} | null;
  preview?: {
    preview_token: string;
    no_changes: boolean;
    changes: Array<{field: string; before: unknown; after: unknown}>;
    proposed_choices: unknown[];
    candidate?: Record<string, unknown>;
    after: {escalations?: Array<{why: string}>};
    affected_decisions: Array<{field: string}>;
  } | null;
};

export type EngineHealth = {
  ok?: boolean;
  data_file?: string;
  release?: string;
  schema?: string;
  quotes_through?: string;
  capabilities?: string[];
};

/**
 * Past-job lookups allowed in flight at once, across every client. A quote page asks
 * for dozens of jobs together (geometry options, accessory history, sources); sent all
 * at once they fill the agents service's admission limit (32) and the rest come back 503.
 */
const JOB_LOOKUP_CONCURRENCY = 6;
let jobLookupsInFlight = 0;
const jobLookupWaiters: Array<() => void> = [];

/** Run a job lookup once a slot is free; a finishing lookup hands its slot straight to the next waiter. */
async function withJobLookupSlot<T>(run: () => Promise<T>): Promise<T> {
  if (jobLookupsInFlight < JOB_LOOKUP_CONCURRENCY) {
    jobLookupsInFlight++;
  } else {
    await new Promise<void>((resolve) => {
      jobLookupWaiters.push(resolve);
    });
  }

  try {
    return await run();
  } finally {
    const next = jobLookupWaiters.shift();
    if (next) next();
    else jobLookupsInFlight--;
  }
}

export class QuotingApiClient extends AuthenticatedApiClient {
  constructor(token?: string) {
    super(token, env.AGENTS_API_URL);
  }

  async readReviewCapabilities(): Promise<{capabilities?: string[]}> {
    return this.request('/api/v1/quoting/health');
  }

  /** The engine's data release: which file, schema and quotes it prices from. */
  async engineHealth(): Promise<EngineHealth> {
    return this.request('/api/v1/quoting/health');
  }

  async readReviews(id: string): Promise<QuoteReadReview[]> {
    return this.request(
      `/api/v1/quoting/requests/${encodeURIComponent(id)}/read-reviews`
    );
  }

  async startReadReview(
    id: string,
    guidance: string,
    asOf: string,
    key: string
  ): Promise<QuoteReadReview> {
    return this.request(
      `/api/v1/quoting/requests/${encodeURIComponent(id)}/read-reviews`,
      {
        method: 'POST',
        body: JSON.stringify({guidance, as_of: asOf, idempotency_key: key}),
      }
    );
  }

  async decideReadReview(
    id: string,
    reviewId: string,
    action: 'apply' | 'discard' | 'refresh',
    previewToken?: string
  ): Promise<QuoteReadReview> {
    return this.request(
      `/api/v1/quoting/requests/${encodeURIComponent(id)}/read-reviews/${encodeURIComponent(reviewId)}/${action}`,
      {
        method: 'POST',
        body: JSON.stringify({preview_token: previewToken}),
      }
    );
  }

  async selectReadReviewSound(
    id: string,
    reviewId: string,
    previewToken: string,
    spectrumIndex: number
  ): Promise<QuoteReadReview> {
    return this.request(
      `/api/v1/quoting/requests/${encodeURIComponent(id)}/read-reviews/${encodeURIComponent(reviewId)}/sound-selection`,
      {
        method: 'POST',
        body: JSON.stringify({
          preview_token: previewToken,
          spectrum_index: spectrumIndex,
        }),
      }
    );
  }

  async previewMailbox(window: MailboxWindow): Promise<MailboxPreview> {
    return this.request('/api/v1/quoting/mailbox/preview', {
      method: 'POST',
      body: JSON.stringify(window),
      timeoutMs: 150_000,
    });
  }

  async intakeMailbox(
    window: MailboxWindow,
    messageIds: string[]
  ): Promise<{committed: true; rows: MailboxRow[]}> {
    return this.request('/api/v1/quoting/mailbox/intake', {
      method: 'POST',
      body: JSON.stringify({...window, message_ids: messageIds}),
      timeoutMs: 150_000,
    });
  }

  async mailboxJobs(ids: string[]): Promise<MailboxJob[]> {
    const params = new URLSearchParams();
    for (const id of ids) params.append('ids', id);
    return this.request(`/api/v1/quoting/intake/jobs?${params.toString()}`, {
      timeoutMs: 150_000,
    });
  }

  async listRequests(): Promise<QuoteQueueItem[]> {
    const rows = await this.request<WireQueueItem[]>(
      '/api/v1/quoting/requests'
    );
    return rows.map((r) => ({
      id: r.id,
      subject: r.subject,
      customer: r.customer,
      receivedAt: r.received_at,
      chip: r.chip,
      nQuestions: r.n_questions,
      engineKey: r.engine_key,
      kw: r.kw,
      nExports: r.n_exports ?? 0,
      progress: r.progress ?? 'open',
      erpQuoteNumber: r.erp_quote_number ?? null,
      assignee: r.assignee ?? null,
      waitingOn: r.waiting_on ?? null,
      progressSource: r.progress_source ?? null,
      etoLevel: r.eto_level ?? null,
      requestType: r.request_type ?? null,
    }));
  }

  async managementIntake(): Promise<ManagementIntake[]> {
    return this.request('/api/v1/quoting/management/intake');
  }

  /** The owner roster (`GET /owners`): AES's Outlook person categories. */
  async quoteOwners(): Promise<string[]> {
    const raw = await this.request<{owners?: unknown}>(
      '/api/v1/quoting/owners'
    );
    return asStrings(raw.owners);
  }

  async intakeSettings(): Promise<IntakeSettings> {
    const raw = await this.request<Record<string, unknown>>(
      '/api/v1/quoting/intake/settings'
    );
    return {
      mailboxPolling: raw.mailbox_polling === true,
      autoRead: raw.auto_read === true,
    };
  }

  /** Set a quote's progress, ERP quote number, assignee, waiting-on or ETO level on its MATIC record. */
  async updateRecord(id: string, changes: QuoteOwnedChanges): Promise<void> {
    const body: Record<string, string> = {};
    if (changes.progress !== undefined) body.progress = changes.progress;
    if (changes.erpQuoteNumber !== undefined)
      body.erp_quote_number = changes.erpQuoteNumber;
    if (changes.assignee !== undefined) body.assignee = changes.assignee;
    if (changes.waitingOn !== undefined) body.waiting_on = changes.waitingOn;
    if (changes.etoLevel !== undefined) body.eto_level = changes.etoLevel;
    await this.request(
      `/api/v1/quoting/requests/${encodeURIComponent(id)}/record`,
      {method: 'PATCH', body: JSON.stringify(body)}
    );
  }

  /** One packet attachment as a File for the viewer, fetched with the bearer token. */
  async attachmentFile(id: string, name: string): Promise<File> {
    const blob = await this.getAttachment(id, name);
    return new File([blob], name, {type: blob.type});
  }

  async getRequest(id: string): Promise<QuotePacket> {
    const raw = await this.request<Record<string, unknown>>(
      `/api/v1/quoting/requests/${encodeURIComponent(id)}`
    );
    return {
      id: asString(raw.id),
      subject: raw.subject as string | undefined,
      from: raw.from as string | undefined,
      receivedAt: raw.received_at as string | undefined,
      customer: raw.customer as string | null | undefined,
      triage: raw.triage as Record<string, unknown> | undefined,
      request: raw.request as Record<string, unknown> | undefined,
      fields: raw.fields as Array<Record<string, unknown>> | undefined,
      openQuestions: raw.open_questions as
        | Array<Record<string, unknown>>
        | undefined,
      attachments: raw.attachments as
        | Array<{name: string; bytes?: number}>
        | undefined,
      lookup: raw.lookup as Record<string, unknown> | null | undefined,
      raw,
    };
  }

  async getThread(id: string): Promise<{
    text: string;
    lines: number;
    /** The desk's clean-thread marks, once the backend carries them (`tail_marks`). */

    tail_marks?: QuoteTailMarks | null;
  }> {
    return this.request(
      `/api/v1/quoting/requests/${encodeURIComponent(id)}/thread`
    );
  }

  async getEvents(id: string): Promise<unknown> {
    return this.request<unknown>(
      `/api/v1/quoting/requests/${encodeURIComponent(id)}/events`
    );
  }

  async listExports(id: string): Promise<string[]> {
    const r = await this.request<{files: string[]}>(
      `/api/v1/quoting/requests/${encodeURIComponent(id)}/exports`
    );
    return r.files;
  }

  /** Fetched with the bearer token: a plain link would navigate without it and 401. */
  async getAttachment(id: string, name: string): Promise<Blob> {
    return this.getBlob(
      `/api/v1/quoting/requests/${encodeURIComponent(id)}/attachments/${encodeURIComponent(name)}`
    );
  }

  async getExport(id: string, name: string): Promise<Blob> {
    return this.getBlob(
      `/api/v1/quoting/requests/${encodeURIComponent(id)}/exports/${encodeURIComponent(name)}`
    );
  }

  /** The written workbook's customer quote page as a PDF; the costing sheets are never printed. */
  async getExportPdf(id: string, name: string): Promise<Blob> {
    return this.getBlob(
      `/api/v1/quoting/requests/${encodeURIComponent(id)}/exports/${encodeURIComponent(name)}/pdf`
    );
  }

  /** `POST /tools/<name>` — exactly what `desk.mjs <name> '<json>'` prints. */
  async tool<T = unknown>(
    name: string,
    args: Record<string, unknown>
  ): Promise<T> {
    try {
      return await this.request<T>(
        `/api/v1/quoting/tools/${encodeURIComponent(name)}`,
        {
          method: 'POST',
          body: JSON.stringify({args}),
        }
      );
    } catch (error) {
      // A desk refusal is 422 `{detail: {errors: [...]}}`; the base client only reads a
      // string detail, so without this the reason is lost and the message is just "422".
      const errors = (
        error as {data?: {detail?: {errors?: unknown}}} | undefined
      )?.data?.detail?.errors;
      if (
        error instanceof EnhancedApiError &&
        Array.isArray(errors) &&
        errors.length > 0
      ) {
        throw new EnhancedApiError(
          errors.map(String).join('; '),
          error.status,
          error.data
        );
      }

      throw error;
    }
  }

  async worksheetState(id: string, asOf: string): Promise<WorksheetState> {
    const raw = await this.tool<Record<string, unknown>>('worksheet.state', {
      id,
      as_of: asOf,
    });
    const cards =
      (raw.cards as Array<Record<string, unknown>> | undefined) ?? [];
    const flags =
      (raw.flags as Array<Record<string, unknown>> | undefined) ?? [];
    // The engine mirrors each open flag as a card (`flag:<event id>`, no options): the
    // page shows the flag itself under "Flagged for review", so the mirror is not a decision.
    const flagCards = new Set(flags.map((f) => `flag:${asString(f.event_id)}`));
    return {
      requestId: asString(raw.request_id) || id,
      lane: asString(raw.lane),
      exportable: Boolean(raw.exportable),
      held: raw.held,
      pins: (raw.pins as Array<Record<string, unknown>> | undefined) ?? [],
      cards: cards
        .filter((c) => !flagCards.has(asString(c.id)))
        .map((c) => ({
          id: asString(c.id),
          stage: asString(c.stage),
          field: asString(c.field),
          question: asString(c.question),
          evidence: (c.evidence as string[] | undefined) ?? [],
          options: (
            (c.options as Array<Record<string, unknown>> | undefined) ?? []
          ).map((o) => ({id: asString(o.id), label: optionLabel(o)})),
        })),
      flags: flags.map((f) => ({
        kind: f.kind === 'review' ? 'review' : undefined,
        shown: asString(f.shown) || undefined,
        stage: asString(f.stage) || undefined,
        eventId: f.event_id as string | undefined,
        field: f.field as string | undefined,
        note: asString(f.note) || undefined,
        by: f.by ? asActor(f.by) : undefined,
        at: asString(f.at) || undefined,
      })),
      pinConflicts: (raw.pin_conflicts as unknown[] | undefined) ?? [],
      spec: (raw.spec as Record<string, unknown> | undefined) ?? {},
      accessories:
        (raw.accessories as Record<string, unknown> | undefined) ?? {},
      raw,
    };
  }

  /** Every worksheet field with the stage it belongs to (`worksheet.fields`, no request). */
  async fieldStages(): Promise<Record<string, string>> {
    const raw = await this.tool<Record<string, unknown>>(
      'worksheet.fields',
      {}
    );
    const map: Record<string, string> = {};
    for (const field of asList(raw.fields)) {
      const name = asString(field.field);
      if (name) map[name] = asString(field.stage);
    }

    return map;
  }

  async flow(id: string, asOf: string): Promise<QuoteFlow> {
    const raw = await this.tool<Record<string, unknown>>('flow', {
      id,
      as_of: asOf,
    });
    const steps =
      (raw.steps as Array<Record<string, unknown>> | undefined) ?? [];
    return {
      requestId: asString(raw.request_id) || id,
      lane: asString(raw.lane),
      exportable: Boolean(raw.exportable),
      escalations: asStrings(raw.escalations),
      steps: steps.map((s) => ({
        id: asString(s.id),
        title: asString(s.title) || asString(s.id),
        value: (s.value as string | null | undefined) ?? null,
        basis: (s.basis as string | null | undefined) ?? null,
        built: Boolean(s.built),
        questions: asList(s.questions).map((q) => ({
          id: asString(q.id),
          field: asString(q.field),
          question: asString(q.question),
          evidence: asStrings(q.evidence),
          choices: asList(q.choices).map((c) => {
            const input = asRecord(c.input);
            return {
              text: asString(c.text),
              input: {
                op: asString(input.op),
                ...(asString(input.field)
                  ? {field: asString(input.field)}
                  : {}),
                ...(input.value === undefined ? {} : {value: input.value}),
                ...(asString(input.option)
                  ? {option: asString(input.option)}
                  : {}),
                ...(asString(input.card_id)
                  ? {card_id: asString(input.card_id)}
                  : {}),
              },
            };
          }),
        })),
        escalations: asStrings(s.escalations),
      })),
      raw,
    };
  }

  /** Full registry discovery; preserve the engine's assessment and selection ref unchanged. */
  async engineConnectionOptions(
    id: string,
    asOf: string
  ): Promise<EngineConnectionChoices> {
    return this.tool<EngineConnectionChoices>(
      'worksheet.engineConnectionOptions',
      {id, as_of: asOf}
    );
  }

  async selectEngineConnection(
    id: string,
    asOf: string,
    choice: EngineConnectionChoice
  ): Promise<Record<string, unknown>> {
    return this.worksheetWrite('worksheet.selectEngineConnection', {
      id,
      as_of: asOf,
      ...choice,
    });
  }

  async selectEngineTurbos(
    id: string,
    asOf: string,
    choice: EngineTurbosChoice
  ): Promise<Record<string, unknown>> {
    return this.worksheetWrite('worksheet.selectEngineTurbos', {
      id,
      as_of: asOf,
      ...choice,
    });
  }

  async accessoryOptions(
    id: string,
    pn: string,
    asOf: string
  ): Promise<AccessorySelectionPreview> {
    return this.tool<AccessorySelectionPreview>('worksheet.accessoryOptions', {
      id,
      pn,
      as_of: asOf,
    });
  }

  async selectAccessory(
    id: string,
    asOf: string,
    action: AccessorySelectionAction
  ): Promise<Record<string, unknown>> {
    return this.worksheetWrite('worksheet.selectAccessory', {
      id,
      as_of: asOf,
      pn: action.pn,
      line_id: action.lineId,
      revision: action.revision,
      ...(action.quantity === undefined ? {} : {qty: action.quantity}),
    });
  }

  async partLookup(pn: string, asOf: string): Promise<QuotePartDetail> {
    return this.tool<QuotePartDetail>('partLookup', {pn, as_of: asOf});
  }

  async outlinePrint(pn: string): Promise<QuoteOutlinePrint> {
    return this.tool<QuoteOutlinePrint>('outlinePrint', {pn});
  }

  /** The print's PDF from the folder staged on the server; 404 when it is not staged. */
  async getOutlinePrintPdf(file: string): Promise<Blob> {
    return this.getBlob(
      `/api/v1/quoting/outline-prints/${encodeURIComponent(file)}`
    );
  }

  async catalogueParts(
    query: CatalogueQuery,
    asOf: string
  ): Promise<CataloguePart[]> {
    return this.tool<CataloguePart[]>('catalogueParts', {query, as_of: asOf});
  }

  /** Historical accessory configurations and their engine-assessed fit, unchanged. */
  async accessoryPrecedent(
    args: Record<string, unknown>
  ): Promise<AccessoryPrecedent> {
    const raw = await this.tool<Record<string, unknown>>(
      'accessoryPrecedent',
      args
    );
    return {
      scopes: asList(raw.scopes).map((scope) => ({
        scope: asString(scope.scope),
        jobs: asNumber(scope.n_scope) ?? 0,
      })),
      types: asList(raw.types).map((type) => ({
        type: asString(type.type),
        configurations: asList(type.configurations).flatMap((config) =>
          config.parsed
            ? asList(config.part_numbers).map((part) => ({
                partNumber: asString(part.part_number),
                description:
                  asString(part.description) || asString(config.description),
                jobs: asNumber(part.n_jobs) ?? 0,
                last: asString(part.last),
                fit: asString(asRecord(config.fits).fit),
                source: asStrings(config.job_keys).join(', '),
                ref: {
                  type: asString(type.type),
                  key: config.key,
                  parsed: config.parsed,
                  part_number: part.part_number,
                },
              }))
            : []
        ),
      })),
    };
  }

  /**
   * The request's class ladder (`requestClassLadder`): the packet in, the ladder the
   * request view computes — every body's rows scored against the required insertion
   * loss, the proposal, the alternatives, and what blocks it. Null when the packet
   * carries no acoustic ask the ladder can score.
   */
  /**
   * The request's class-strip evidence (`requestClassEvidence`): per class on the ladder its
   * historical silencer-only price, and the past requests AES quoted near this ask, grouped as the
   * desk groups them. Null when the packet carries no acoustic ask.
   */
  async requestClassEvidence(
    packet: Record<string, unknown>
  ): Promise<QuoteClassEvidence | null> {
    const raw = await this.tool<Record<string, unknown>>(
      'requestClassEvidence',
      packet
    );
    if (!raw.evidence || typeof raw.evidence !== 'object') return null;
    const evidence = asRecord(raw.evidence);
    return {
      sameDb: asNumber(evidence.same_db),
      nearbyDb: asNumber(evidence.nearby_db),
      thisNeededDb: asNumber(evidence.this_needed_db),
      requests: asList(evidence.requests).map((r) => ({
        group: asString(r.group) as QuoteEvidenceGroup,
        jobKey: asString(r.job_key),
        quoteNumber: asString(r.quote_number) || null,
        date: asString(r.date) || null,
        customer: asString(r.customer) || null,
        kw: asNumber(r.kw),
        targetDbA: asNumber(r.target_db_a),
        distanceFt: asNumber(r.distance_ft),
        neededDb: asNumber(r.needed_db),
        deltaDb: asNumber(r.delta_db),
        body: asString(r.body),
        classNum: asNumber(r.class_num),
        requirement: asString(r.requirement) || null,
        dims: bodyDims(r.dims),
        price: asNumber(r.price),
        engineKey: asString(r.engine_key) || null,
      })),
      engineKey: asString(evidence.engine_key) || null,
      family: asString(evidence.family) || null,
      prices: asList(evidence.prices).map((p) => {
        const history = asRecord(p.history);
        return {
          body: asString(p.body),
          classNum: asNumber(p.class_num) ?? 0,
          median: asNumber(history.median),
          low: asNumber(history.low),
          high: asNumber(history.high),
          total: asNumber(history.total) ?? 0,
        };
      }),
      bands: readBandCalc(evidence.bands),
    };
  }

  async requestClassLadder(
    packet: Record<string, unknown>
  ): Promise<QuoteClassLadder | null> {
    const raw = await this.tool<Record<string, unknown>>(
      'requestClassLadder',
      packet
    );
    if (!raw.ladder || typeof raw.ladder !== 'object') return null;
    const ladder = asRecord(raw.ladder);
    const sound = asRecord(ladder.sound);
    const ask = asRecord(ladder.ask_used);
    const sourceLevel = asRecord(ask.source_level);
    const usual = asRecord(asRecord(raw.context).aes_usual);
    const ladders: Record<string, QuoteLadderRow[]> = {};
    for (const [body, rows] of Object.entries(asRecord(ladder.ladders))) {
      ladders[body] = asList(rows).map((row) => readLadderRow(row));
    }

    return {
      status: asString(ladder.status),
      code: asString(ladder.code) || null,
      sound: {
        sourceId: asString(sound.source_id) || null,
        status: asString(sound.status),
        caveat: asString(sound.caveat) || null,
      },
      ask: {
        lane: asString(ask.lane),
        targetDbA: asNumber(ask.target_db_a),
        distanceFt: asNumber(ask.distance_ft),
        distanceBasis: asString(ask.distance_basis),
        reductionDb: asNumber(ask.reduction_db),
        grade: asString(ask.grade) || null,
        statedShape: asString(ask.stated_shape) || null,
        location: asString(ask.location) || null,
        locationBasis: asString(ask.location_basis),
        targetUsed: asNumber(ask.target_used),
        requiredIlDb: asNumber(ask.required_il_db),
        sourceLevel:
          asNumber(sourceLevel.raw_db_a) === null
            ? null
            : {
                rawDbA: asNumber(sourceLevel.raw_db_a)!,
                refDistanceFt: asNumber(sourceLevel.ref_distance_ft) ?? 0,
              },
      },
      recommendedBody: asString(ladder.recommended_body) || null,
      classNum: asNumber(ladder.class_num),
      rowId: asString(ladder.row_id) || null,
      proposal: readProposal(ladder.proposal),
      alternatives: asList(ladder.alternatives)
        .map((alternative) => readProposal(alternative))
        .filter((alternative) => alternative !== null),
      ladders,
      openSlots: asList(ladder.open_slots).map((slot) => ({
        id: asString(slot.id),
        field: asString(slot.field),
        question: asString(slot.question),
        wouldCloseIt: asString(slot.would_close_it),
        disposition: asString(slot.disposition),
      })),
      aesUsual: asString(usual.class_label)
        ? {
            classLabel: asString(usual.class_label),
            nJobs: asNumber(usual.n_jobs) ?? 0,
            share: asNumber(usual.share) ?? 0,
          }
        : null,
      raw,
    };
  }

  /** `job`: one past AES quote's identity and the part number it carried. */
  async job(jobKey: string): Promise<QuoteJobSummary> {
    const raw = await withJobLookupSlot(async () =>
      this.tool<Record<string, unknown>>('job', {job_key: jobKey})
    );
    const fields = asRecord(raw.fields);
    return {
      jobKey,
      quoteNumber: asString(fields.quote_number) || null,
      customer: asString(fields.company) || asString(fields.customer) || null,
      date: asString(fields.date) || null,
      partNumber: asString(fields.part_number) || null,
      mounting:
        asString(fields.mounting)
          .split(/\s+/)
          .filter(Boolean)
          .join(' ')
          .toUpperCase() || null,
      engineKey: asString(fields.engine_key) || null,
      kw: asNumber(fields.kw),
      requirement: asString(fields.requirement) || null,
      silencerType: asString(fields.silencer_type) || null,
      linePartNumbers: asList(raw.lines)
        .filter((line) => line.kind === 'quote_line')
        .map((line) => asString(line.part_number).trim())
        .filter(Boolean),
      lineCategories: asList(raw.lines)
        .filter((line) => line.kind === 'quote_line')
        .map((line) => asString(line.category).trim().toUpperCase())
        .filter(Boolean),
    };
  }

  /** `engineProfile`: every kW rating AES's sound data holds for the engine, ascending. */
  async engineRatings(engineKey: string): Promise<number[]> {
    const raw = await this.tool<Record<string, unknown>>('engineProfile', {
      engine_key: engineKey,
    });
    return asList(raw.ratings)
      .map((rating) => asNumber(rating.kw))
      .filter((kw): kw is number => kw !== null);
  }

  /** `resolveSound`: the spectrum the engine reads for an engine at a rating. */
  async resolveSound(
    engineKey: string,
    kw: number,
    asOf?: string
  ): Promise<QuoteSound> {
    const raw = await this.tool<Record<string, unknown>>('resolveSound', {
      engine_key: engineKey,
      kw,
      ...(asOf ? {as_of: asOf} : {}),
    });
    return {
      status: asString(raw.status),
      engineKey: asString(raw.engine_key) || engineKey,
      requestedKw: asNumber(raw.requested_kw) ?? kw,
      selected: readSoundCandidate(raw.selected),
      alternatives: asList(raw.alternatives)
        .map((candidate) => readSoundCandidate(candidate))
        .filter((candidate) => candidate !== null),
      selectionBasis: asString(raw.selection_basis) || null,
      caveat: asString(raw.caveat) || null,
      openQuestion: asString(asRecord(raw.open_question).question) || null,
    };
  }

  /** A person's worksheet write; the backend stamps the caller as actor. */
  async worksheetWrite(
    name:
      | 'worksheet.selectAccessory'
      | 'worksheet.selectEngineConnection'
      | 'worksheet.selectEngineTurbos'
      | 'worksheet.set'
      | 'worksheet.choose'
      | 'worksheet.flag'
      | 'worksheet.hold'
      | 'worksheet.undo'
      | 'worksheet.clear_flag',
    args: Record<string, unknown>
  ): Promise<Record<string, unknown>> {
    return this.tool<Record<string, unknown>>(name, args);
  }

  /**
   * The EEAA export plan: the quote header the engine prefilled (company, contact,
   * reference, date — each with its source) and the file it would write. The engine
   * refuses while anything is stale, held or open.
   */
  async exportPlan(id: string, asOf: string): Promise<QuoteExportPlan> {
    const raw = await this.tool<Record<string, unknown>>(
      'worksheet.export_eeaa',
      {id, as_of: asOf}
    );
    const header = asRecord(raw.header);
    const sources = asRecord(header.sources);
    return {
      exportable: Boolean(raw.exportable),
      filename: asString(raw.filename),
      header: Object.fromEntries(
        EXPORT_HEADER_FIELDS.map((key) => [key, asString(header[key])])
      ) as Record<ExportHeaderField, string>,
      sources: Object.fromEntries(
        EXPORT_HEADER_FIELDS.map((key) => [
          key,
          asString(asRecord(sources[key]).basis),
        ])
      ) as Record<ExportHeaderField, string>,
    };
  }

  /**
   * Write the EEAA workbook into the request's exports: the plan's header as the person
   * confirmed it (`confirmed: true` is what the engine checks) and `out` the file name the
   * plan proposed (engine, rating, date, class), so AES can tell one quote's file from another.
   */
  async exportEeaa(
    id: string,
    asOf: string,
    header: Record<ExportHeaderField, string>,
    filename: string
  ): Promise<Record<string, unknown>> {
    return this.tool<Record<string, unknown>>('worksheet.export_eeaa', {
      id,
      as_of: asOf,
      header: {...header, confirmed: true},
      out: filename,
    });
  }

  /** The MATIC record for a request (the Quote ObjectType's row), or null while it is unwritten. */
  async getRecord(id: string): Promise<QuoteRecordRef | null> {
    try {
      const raw = await this.request<Record<string, unknown>>(
        `/api/v1/quoting/requests/${encodeURIComponent(id)}/record`
      );
      return {
        objectTypeId: asString(raw.object_type_id),
        recordId: asString(raw.record_id),
        code: asString(raw.code),
      };
    } catch (error) {
      if (error instanceof Error && /not found|404/i.test(error.message))
        return null;
      throw error;
    }
  }

  /**
   * A Relay thread into the engine's reader (`POST /import/thread/{id}`): the slug the request
   * will carry once Otto finishes and publishes the read, and the prepare result.
   */
  async importThread(
    threadId: string,
    runId = 'hosted'
  ): Promise<QuoteThreadImport> {
    const raw = await this.request<Record<string, unknown>>(
      `/api/v1/quoting/import/thread/${encodeURIComponent(threadId)}`,
      {method: 'POST', body: JSON.stringify({run_id: runId})}
    );
    return {
      slug: asString(raw.slug),
      runId: asString(raw.run_id) || runId,
      subject: asString(raw.subject),
      attachments: asStrings(raw.attachments),
      skippedAttachments: asStrings(raw.skipped_attachments),
    };
  }

  /** Queue a Relay thread for hosted intake's full read (`POST /intake/threads/{id}/promote`). */
  async promoteThread(threadId: string): Promise<QuoteThreadPromotion> {
    const raw = await this.request<Record<string, unknown>>(
      `/api/v1/quoting/intake/threads/${encodeURIComponent(threadId)}/promote`,
      {method: 'POST'}
    );
    const status = asString(raw.status);
    if (status === 'intake_disabled') return {status};
    if (status === 'pending' || status === 'retry' || status === 'reading')
      return {status, jobId: asString(raw.job_id)};
    throw new Error(`Unexpected promote status: ${status}`);
  }

  async importRequest(body: {
    slug: string;
    runId?: string;
    messageText?: string;
    meta?: Record<string, unknown>;
    attachments?: Array<{name: string; content_base64: string}>;
  }): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>('/api/v1/quoting/import', {
      method: 'POST',
      body: JSON.stringify({
        slug: body.slug,
        run_id: body.runId ?? 'hosted',
        ...(body.messageText === undefined
          ? {}
          : {
              message_text: body.messageText,
              meta: body.meta ?? {},
              attachments: body.attachments ?? [],
            }),
      }),
    });
  }
}

const clientCache = new Map<string, QuotingApiClient>();
export function getQuotingApi(token?: string): QuotingApiClient {
  const key = token ?? 'no-token';
  if (!clientCache.has(key)) clientCache.set(key, new QuotingApiClient(token));
  return clientCache.get(key)!;
}

/* eslint-enable @typescript-eslint/naming-convention */
